import { ReactElement, useMemo } from 'react';
import { DataMode, buildRatioForceScatter, maxXValue } from './auxSensorChannels';
import { DEFAULT_PAIR_LABEL } from './auxPlotStyle';
import AuxRatioScatterCard, { RatioScatterAxis } from './AuxRatioScatterCard';

interface AuxRatioForcePlotProps {
  gridded: Record<string, (number | null)[]> | undefined;
  circumference: number;
  /** Min linear speed (m/s) — samples below are masked (shared slider). */
  threshold: number;
  dataMode?: DataMode;
  isLoading?: boolean;
  /** Names the two wheel sensors the ratio is between — see AuxRatioTimePlot. */
  pairLabel?: string;
}

/**
 * Slip-vs-tractive-force scatter for the two wheel sensors. X is the tyre driving
 * force (rider power / ground speed, in N); the fit window spans the run's full
 * force range. Plotting, masking, the fit and "Zoom to window" are
 * `AuxRatioScatterCard`'s.
 */
export default function AuxRatioForcePlot({
  gridded,
  circumference,
  threshold,
  dataMode = 'all',
  isLoading = false,
  pairLabel = DEFAULT_PAIR_LABEL,
}: AuxRatioForcePlotProps): ReactElement {
  const scatter = useMemo(
    () => buildRatioForceScatter(gridded, circumference, threshold, dataMode),
    [gridded, circumference, threshold, dataMode],
  );
  const maxForce = useMemo(() => maxXValue(scatter.included), [scatter]);
  const axis = useMemo<RatioScatterAxis>(() => ({
    title: 'Tyre Driving Force (N)',
    unit: 'N',
    tooltipDigits: 1,
    fitWindow: { label: 'Driving force window', max: maxForce },
    format: { slopeUnit: '/N', slopeDigits: 6, offsetDigits: 4 },
  }), [maxForce]);

  return (
    <AuxRatioScatterCard
      title="Slip Ratio vs Tyre Driving Force"
      storageKey="ratioForce"
      scatter={scatter}
      axis={axis}
      threshold={threshold}
      pairLabel={pairLabel}
      emptyMessage="No slip-ratio samples for this run (needs two wheel sensors + power)."
      isLoading={isLoading}
    />
  );
}
